// src/features/analytics/WidgetDialog.tsx
// Create / edit a widget on a dashboard. CreateWidget, UpdateWidget, GetWidget.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Textarea, Button, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { analyticsApi } from '@core/api';
import type { AvailableDataSource, DataSourceConfig, Widget, WidgetType } from '@core/types/analytics';
import { AnalyticsModal } from './Modal';
import { DataSourceConfigEditor } from './DataSourceConfigEditor';
import { WIDGET_TYPES, errMessage, makeEntity, type ScopeIds } from './helpers';

const EMPTY_DATA_SOURCE: DataSourceConfig = {
  product_slug: '',
  entity_type: '',
  metrics: [],
  filters: [],
  group_by: [],
  order_by: '',
  order_direction: 'desc',
  limit: 0,
};

interface WidgetDialogProps {
  open: boolean;
  onClose: () => void;
  dashboardId: string;
  scope: ScopeIds;
  dataSources: AvailableDataSource[];
  widgetId?: string;
  onSaved: (widget: Widget) => void;
}

export function WidgetDialog({ open, onClose, dashboardId, scope, dataSources, widgetId, onSaved }: WidgetDialogProps) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [widgetType, setWidgetType] = useState<WidgetType>('WIDGET_TYPE_METRIC');
  const [dataSource, setDataSource] = useState<DataSourceConfig>(EMPTY_DATA_SOURCE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (widgetId) {
      setLoading(true);
      analyticsApi
        .GetWidget(widgetId)
        .then((res) => {
          const w = res.widget;
          setName(w?.name ?? '');
          setDescription(w?.description ?? '');
          setWidgetType(w?.widget_type ?? 'WIDGET_TYPE_METRIC');
          setDataSource(w?.data_source ?? EMPTY_DATA_SOURCE);
        })
        .catch((e: unknown) => setError(errMessage(e, t('analytics.widgets.loadError'))))
        .finally(() => setLoading(false));
    } else {
      setName('');
      setDescription('');
      setWidgetType('WIDGET_TYPE_METRIC');
      setDataSource(EMPTY_DATA_SOURCE);
    }
  }, [open, widgetId, t]);

  const save = async () => {
    if (!name.trim()) {
      setError(t('analytics.widgets.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (widgetId) {
        const res = await analyticsApi.UpdateWidget(widgetId, {
          widget: {
            entity: makeEntity(widgetId, 'widget', scope),
            name: name.trim(),
            description,
            dashboard: makeEntity(dashboardId, 'dashboard', scope),
            widget_type: widgetType,
            data_source: dataSource,
            refresh_interval_seconds: 0,
          },
        });
        if (res.widget) onSaved(res.widget);
      } else {
        const res = await analyticsApi.CreateWidget(dashboardId, {
          entity: makeEntity('', 'widget', scope),
          dashboard: makeEntity(dashboardId, 'dashboard', scope),
          name: name.trim(),
          description,
          widget_type: widgetType,
          data_source: dataSource,
        });
        if (res.widget) onSaved(res.widget);
      }
      onClose();
    } catch (e) {
      setError(errMessage(e, t('analytics.widgets.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnalyticsModal
      open={open}
      onClose={onClose}
      title={widgetId ? t('analytics.widgets.edit') : t('analytics.widgets.create')}
      widthClass="max-w-3xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} disabled={saving || loading}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      {loading ? (
        <div className="space-y-2" aria-busy="true">
          <div className="h-8 animate-pulse rounded-md bg-muted" />
          <div className="h-24 animate-pulse rounded-md bg-muted" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('analytics.widgets.name')} value={name} onChange={(e) => setName(e.target.value)} required />
            <Select label={t('analytics.widgets.type')} value={widgetType} onChange={(e) => setWidgetType(e.target.value as WidgetType)}>
              {WIDGET_TYPES.map((wt) => (
                <option key={wt} value={wt}>
                  {t(`analytics.widgetType.${wt}`)}
                </option>
              ))}
            </Select>
          </div>
          <Textarea label={t('analytics.widgets.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          <DataSourceConfigEditor value={dataSource} onChange={setDataSource} dataSources={dataSources} />
        </div>
      )}
    </AnalyticsModal>
  );
}
